// Web Push helpers · Transfiérelo
// Patrones siguen el playbook MDN/web.dev. Sólo se invocan suscripciones
// como respuesta a un gesto explícito del usuario (Apple/iOS requirement).

import api from "@/lib/api";

const STORAGE_FLAGS = "tf_push_flags_v1";

const flags = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_FLAGS)) || {}; } catch { return {}; }
};
const writeFlags = (next) => {
  try { localStorage.setItem(STORAGE_FLAGS, JSON.stringify({ ...flags(), ...next })); } catch { /* noop */ }
};

export const pushSupport = {
  get serviceWorker() { return typeof navigator !== "undefined" && "serviceWorker" in navigator; },
  get pushManager() { return typeof window !== "undefined" && "PushManager" in window; },
  get notifications() { return typeof window !== "undefined" && "Notification" in window; },
  get supported() {
    return this.serviceWorker && this.pushManager && this.notifications;
  },
  get permission() {
    return this.notifications ? Notification.permission : "denied";
  },
};

export const pushFlags = {
  read: flags,
  dismiss() { writeFlags({ dismissedAt: Date.now() }); },
  clearDismiss() { writeFlags({ dismissedAt: 0 }); },
  shouldShowBanner() {
    if (!pushSupport.supported) return false;
    if (this.read().subscribedAt) return false;
    if (pushSupport.permission !== "default") return false;
    const d = this.read().dismissedAt || 0;
    if (Date.now() - d < 1000 * 60 * 60 * 24 * 3) return false; // 3 días de cooldown
    return true;
  },
  markSubscribed() { writeFlags({ subscribedAt: Date.now() }); },
  clearSubscribed() { writeFlags({ subscribedAt: 0 }); },
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const registerServiceWorker = async () => {
  if (!pushSupport.serviceWorker) return null;
  try {
    const reg = await navigator.serviceWorker.register("/service-worker.js", { scope: "/" });
    return reg;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[push] sw register failed", err);
    return null;
  }
};

export const enablePush = async () => {
  if (!pushSupport.supported) {
    throw new Error("Tu navegador no soporta notificaciones push.");
  }
  // Permiso explícito (Apple iOS 16.4+: requiere PWA instalada + gesto)
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    pushFlags.dismiss();
    throw new Error(permission === "denied" ? "Habilita las notificaciones en los ajustes del navegador para activarlas." : "Permiso de notificaciones no concedido.");
  }

  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) reg = await registerServiceWorker();
  await navigator.serviceWorker.ready;

  // Obtener la clave pública VAPID desde el backend (no hardcodeada en el bundle).
  const vapidEnv = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  let publicKey = vapidEnv;
  if (!publicKey) {
    const { data } = await api.get("/api/push/vapid-key");
    publicKey = data?.publicKey;
  }
  if (!publicKey) throw new Error("Falta la clave pública del servidor de notificaciones.");

  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await api.post("/api/push/subscribe", existing.toJSON());
    pushFlags.markSubscribed();
    return existing;
  }

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await api.post("/api/push/subscribe", sub.toJSON());
  pushFlags.markSubscribed();
  return sub;
};

export const disablePush = async () => {
  if (!pushSupport.serviceWorker) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) { pushFlags.clearSubscribed(); return; }
  try {
    await api.delete("/api/push/subscribe", { data: { endpoint: sub.endpoint } });
  } catch (_) { /* ignore */ }
  await sub.unsubscribe();
  pushFlags.clearSubscribed();
};

// Hook ligero — escucha mensajes del SW para redirigir tras click de notificación.
export const installPushNavigationBridge = (navigate) => {
  if (!pushSupport.serviceWorker) return () => {};
  const handler = (event) => {
    if (event?.data?.type === "TF_NAVIGATE" && event.data.url && typeof navigate === "function") {
      try { navigate(event.data.url); } catch { /* noop */ }
    }
  };
  navigator.serviceWorker.addEventListener("message", handler);
  return () => navigator.serviceWorker.removeEventListener("message", handler);
};
